import { PageChangeEvent, Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import {
  toolbarPlugin,
  ToolbarSlot,
  TransformToolbarSlot,
} from "@react-pdf-viewer/toolbar";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import React, { useEffect, useState } from "react";

import {
  pageNavigationPlugin,
  RenderGoToPageProps,
} from "@react-pdf-viewer/page-navigation";

import { IonButton } from "@ionic/react";
import { PDFDocument } from "pdf-lib";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const getPDFTrunckated = async (pdfPath: string, pageNumbers: number[]) => {
  const mergedPdf = await PDFDocument.create();

  for (const pageNumber of pageNumbers) {
    const pdfBytes = await fetch(pdfPath).then((res) => res.arrayBuffer());
    const sourcePdf = await PDFDocument.load(pdfBytes);
    const [copiedPage] = await mergedPdf.copyPages(sourcePdf, [pageNumber]);
    mergedPdf.addPage(copiedPage);
  }

  return mergedPdf.save();
};

const getNumberArr = (initialValue: number, endPage: number) => {
  const arr = [];

  for (let i = initialValue; i < endPage; i++) {
    arr.push(i);
  }

  return arr;
};

const empty = () => <></>;

interface Props {
  fileURL: string;
  setBookLoader: (isLoad: boolean) => void;
  initialPage?: number;
  endPage?: number;
  goToTop?: boolean;
  label?: string;
  onHitLastPage?: () => void;
  getNbPage?: (page: number) => void;
}

const PDFViewer: React.FC<Props> = ({
  fileURL,
  setBookLoader,
  initialPage,
  endPage,
  goToTop = true,
  label = "Sommaire",
  onHitLastPage = () => null,
  getNbPage = () => null,
}) => {
  const [PDF, setPDF] = useState<Uint8Array | string>();

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { GoToFirstPage } = pageNavigationPluginInstance;

  const toolbarPluginInstance = toolbarPlugin({
    pageNavigationPlugin: {
      enableShortcuts: true,
    },
  });
  const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;
  const [defaultScale, setDefaultScale] = useState(2.4);

  useEffect(() => {
    getPDFPageCount(fileURL).then((res: number) => getNbPage(res));
    function handleResize() {
      const screenWidth = window.innerWidth;
      if (screenWidth < 600) {
        setDefaultScale(0.4);
      } else if (screenWidth >= 600 && screenWidth < 1400) {
        setDefaultScale(1.2);
      } else {
        setDefaultScale(2.2);
      }
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function getPDFPageCount(pdfUrl: string): Promise<number> {
    try {
      const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
      return pdf.numPages;
    } catch (error) {
      return -1;
      console.error("Error loading PDF:", error);
      throw new Error("Failed to load PDF.");
    }
  }

  const transform: TransformToolbarSlot = (slot: ToolbarSlot) => ({
    ...slot,
    EnterFullScreen: empty,
    Download: empty,
    Print: empty,
    Open: empty,
    SwitchTheme: empty,
    Search: empty,
    ShowSearchPopover: () => (
      <GoToFirstPage>
        {(props: RenderGoToPageProps) => (
          <IonButton
            style={{
              "--border-color": "var(--ion-color-secondary)",
              "--border-style": "solid",
              "--border-width": "2px",
              "--color": "var(--ion-color-secondary)",
            }}
            shape="round"
            onClick={props.onClick}
          >
            {label}
          </IonButton>
        )}
      </GoToFirstPage>
    ),
    OpenMenuItem: empty,
    DownloadMenuItem: empty,
    PrintMenuItem: empty,
    SwitchThemeMenuItem: empty,
    NumberOfPages: empty,
    CurrentPageInput: empty,
  });

  const handleChangePage: (event: PageChangeEvent) => void = ({
    doc,
    currentPage,
  }) => {
    if (doc.numPages - 1 === currentPage) onHitLastPage();
  };

  useEffect(() => {
    if (initialPage !== undefined && endPage !== undefined) {
      getPDFTrunckated(fileURL, getNumberArr(initialPage, endPage)).then(
        (res) => {
          setPDF(res);
        }
      );
    } else {
      setPDF(fileURL);
    }
  }, [initialPage, endPage, fileURL]);

  return (
    <>
      {PDF ? (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <div
            className="js-viewer-container rpv-core__viewer"
            style={{
              border: "1px solid rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <div
              style={{
                alignItems: "center",
                backgroundColor: "#eeeeee",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                padding: "0.25rem",
              }}
            >
              <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
            </div>

            <div
              style={{
                flex: 1,
                overflow: "hidden",
                backgroundColor: "white",
              }}
            >
              <Viewer
                defaultScale={defaultScale}
                enableSmoothScroll={false}
                fileUrl={PDF}
                onDocumentLoad={(e) => {
                  setBookLoader(false);
                }}
                plugins={[toolbarPluginInstance, pageNavigationPluginInstance]}
                onPageChange={handleChangePage}
              />
            </div>
          </div>
        </Worker>
      ) : (
        <div>Chargement</div>
      )}
    </>
  );
};

export default PDFViewer;
